<template>
    <div class="ConditionEditor">
        <div
            v-for="(condition, conditionIndex) in itemList"
            :key="conditionIndex"
            ref="Conditions"
        >
            <div class="condition" :class="conditionIndex === 0 ? 'mt-0' : ''">
                <div class="indicator centerY">
                    <span class="tag-condition">AND</span>
                    <div
                        v-if="isEdit"
                        class="btn-icon-2 mt-0 ml-6"
                        style="background: #e5e5e5; height: 26px; width: 26px"
                        @click="removeCondition(conditionIndex)"
                    >
                        <img src="/img/scenario/close-fill.svg" alt="icon-close-group-condition">
                    </div>
                </div>
                <div class="condition-body mt-8">
                    <div
                        v-if="condition.length === 0 && !isEdit"
                        style="color: #727885"
                    >
                        {{ $f.tc('noDataAvailable') }}
                    </div>
                    <v-layout
                        v-for="(operation, operationIndex) in condition"
                        :key="operationIndex"
                        class="mb-8"
                        align-start
                        spellcheck="false"
                    >
                        <v-tooltip bottom nudge-bottom="-6">
                            <template v-slot:activator="{ on }">
                                <div v-on="on" style="width: 150px">
                                    <v-autocomplete
                                        outlined hide-details
                                        :items="sourceList"
                                        class="input-custom mr-6"
                                        v-model="operation.variable"
                                        @focusout="focusOut"
                                        :readonly="!isEdit"
                                        spellcheck="false"
                                    ></v-autocomplete>
                                </div>
                            </template>
                            <span>{{ operation.variable }}</span>
                        </v-tooltip>
                        <div class="operator">
                            <v-select
                                :items="OperatorList"
                                class="input-custom mr-6"
                                v-model="operation.operator"
                                @input="focusOut"
                                :readonly="!isEdit"
                                hide-details
                                dense
                            ></v-select>
                        </div>
                        <div style="flex: 1">
                            <ScenarioComboBox
                                v-if="shouldSelectMultipleValue(operation)"
                                v-model="operation.value"
                                :items="operation.variable === '$intent' ? availableIntentList : []"
                                :multiple="true"
                                :readonly="!isEdit"
                                @save="focusOut"
                                style="width: 100%"
                            ></ScenarioComboBox>
                            <v-text-field
                                v-else
                                hide-details
                                v-model="operation.value"
                                class="input-custom"
                                :readonly="!isEdit"
                                @focusout="focusOut"
                                style="width: 100%"
                            ></v-text-field>
                        </div>
                        <div
                            v-if="isEdit"
                            class="btn-icon-2 ml-6"
                            @click="removeOperation(conditionIndex, operationIndex)"
                        >
                            <img src="/img/scenario/delete-new_red.svg" alt="icon-delete-variable" style="width: 18px">
                        </div>
                    </v-layout>
                    <v-layout align-start v-if="isEdit">
                        <div style="width: 150px">
                            <v-autocomplete
                                :items="sourceList"
                                :placeholder="$f.tc('variable')"
                                class="input-custom mr-6"
                                v-model="modelList[conditionIndex].variable"
                                hide-details
                                dense clearable
                                ref="NewVariable"
                            ></v-autocomplete>
                        </div>
                        <div class="operator">
                            <v-select
                                :items="OperatorList"
                                class="input-custom mr-6"
                                v-model="modelList[conditionIndex].operator"
                                hide-details
                                dense
                            ></v-select>
                        </div>
                        <div style="flex: 1">
                            <ScenarioComboBox
                                v-if="shouldSelectMultipleValue(modelList[conditionIndex].operator)"
                                v-model="modelList[conditionIndex].value"
                                :items="modelList[conditionIndex].variable === '$intent' ? availableIntentList : []"
                                :multiple="true"
                                style="width: 100%"
                            ></ScenarioComboBox>
                            <ScenarioInput
                                v-else
                                v-model="modelList[conditionIndex].value"
                                style="width: 100%"
                            ></ScenarioInput>
                        </div>
                        <div class="btn-icon-2 ml-6" @click="addOperation(conditionIndex)">
                            <img src="/img/scenario/add_grey.svg" alt="icon-add-condition" style="width: 16px">
                        </div>
                    </v-layout>
                </div>
            </div>
            
            <v-layout v-if="conditionIndex < itemList.length - 1" style="position: relative">
                <div class="line"></div>
                <div class="indicator-or">OR</div>
            </v-layout>
        </div>
        
        <v-layout class="mt-8">
            <div v-if="isEdit" class="btn-add-group" @click="addCondition">
                <div class="icon-add-group"></div>
                AND Group
            </div>
        </v-layout>
        
        <div v-if="!isEdit && itemList.length === 0" style="font-size: 14px; color: #727885">
            {{ $f.tc('noConditions') }}
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import ScenarioInput from '@/views/Scenario/components/ScenarioInput'
import ScenarioService from '@/services/scenario.service'
import lodash from 'lodash'
import ScenarioComboBox from '@/views/Scenario/components/ScenarioComboBox'

const { OperatorType, OperatorList } = ScenarioService

export default {
    name: 'ConditionEditor',
    components: { ScenarioComboBox, ScenarioInput },
    props: {
        items: Array
    },
    data () {
        return {
            OperatorType,
            OperatorList,
            itemList: [],
            test: '',
            systemIntentList: [
                { text: '$unrecognize', value: '$unrecognize' }
            ],
            modelList: []
        }
    },
    computed: {
        ...mapState({
            intentList: state => state.scenario.intentList,
            entityList: state => state.scenario.entityList,
            variableList: state => state.scenario.variableList,
        }),
        isEdit () {
            return ScenarioService.isEditable() && ScenarioService.isModeEditing()
        },
        availableIntentList () {
            return [
                ...lodash.orderBy(this.intentList.map(item => ({
                    text: item.label,
                    value: item.label
                })), item => item.text),
                ...this.systemIntentList
            ]
        },
        sourceList () {
            let list = [
                ...this.variableList.map(item => ({ text: item.name, value: item.name })),
                ...this.entityList.map(item => ({ text: item.label, value: item.label })),
                { text: '$intent', value: '$intent' },
                { text: '$countReprompt', value: '$countReprompt' }
            ]
            list = this.$lodash.uniqBy(list, 'value')
            list = this.$lodash.orderBy(list, 'text')
            return list
        }
    },
    methods: {
        isHasEmptyCondition () {
            return !(!this.itemList.find(i => i.length === 0))
        },
        addCondition () {
            if (this.isHasEmptyCondition()) {
                this.$appService.toastError(this.$f.tc('itemFieldRequired', { item: this.$f.tc('variable') }))
                return
            }
            this.itemList.push([])
            this.focusOut()
        },
        removeCondition (index) {
            this.itemList.splice(index, 1)
            this.focusOut()
        },
        addOperation (conditionIndex) {
            let model = this.modelList[conditionIndex]
            if (model.variable) {
                this.itemList[conditionIndex].push(model)
                this.modelList[conditionIndex] = {
                    variable: '',
                    operator: OperatorType.EQUAL,
                    value: ''
                }
                this.focusOut()
            }
        },
        removeOperation (conditionIndex, operationIndex) {
            if (Array.isArray(this.itemList[conditionIndex])) {
                this.itemList[conditionIndex].splice(operationIndex, 1)
            } else {
                this.itemList[conditionIndex] = []
            }
            if (this.itemList[conditionIndex].length === 0) {
                this.itemList[conditionIndex] = []
            }
            this.focusOut()
        },
        focusOut () {
            if (this.isEdit) {
                this.$emit('save')
            }
        },
        shouldSelectMultipleValue (operation) {
            let operator = typeof operation === 'object' ? operation.operator : operation
            return operator === OperatorType.IN || operator === OperatorType.NOT_IN
        },
    },
    watch: {
        items: {
            immediate: true,
            handler: function (items) {
                this.itemList = items ? items : []
                let list = []
                for (let i in this.itemList) {
                    if (i >= this.modelList.length) {
                        list.push({
                            variable: '',
                            operator: OperatorType.EQUAL,
                            value: ''
                        })
                    } else {
                        list.push(this.modelList[i])
                    }

                }
                this.modelList = list
            }
        },
        itemList: {
            deep: true,
            handler: function (itemList) {
                if (JSON.stringify(itemList) !== JSON.stringify(this.items)) {
                    this.$emit('update:items', itemList)
                    this.$forceUpdate()
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.btn-fab {
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #ccc;
    cursor: pointer;

    &.active {
        background: #ccc;
    }
}

.condition {
    position: relative;
    padding: 12px;
    background: #f2f2f2cc;
    border-radius: 5px;
    margin-top: 8px;

    .delete {
        position: absolute;
        right: 0;
        top: 0;
        background: #fff;
        border-left: 1px solid #EDEDF2;
        border-bottom: 1px solid #EDEDF2;
        border-radius: 0 5px 0 5px;
        padding: 1px;
    }

    .plus {
        position: absolute;
        right: 0;
        bottom: 0;
        background: #fff;
        border-left: 1px solid #EDEDF2;
        border-top: 1px solid #EDEDF2;
        border-radius: 5px 0 5px 0;
        padding: 1px;
    }
}

.condition-body {
    font-family: 'open sans', serif;
    font-size: 14px;
}

.indicator {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-family: "open sans semi", serif;
    
    .tag-condition {
        padding: 2px 8px;
        border-radius: 5px;
        border: 1px solid #d9d9d9;
        color: #727885;
        font-size: 13px;
        font-family: 'open sans bold', serif;
        background: #fff;
    }
}

.indicator-or {
    margin-top: 8px;
    margin-left: 12px;
    padding: 2px 8px;
    border-radius: 5px;
    color: #fff;
    font-size: 13px;
    font-family: 'open sans bold', serif;
    background: #727885;
    z-index: 2;
}

.line {
    position: absolute;
    height: 42px;
    width: 1px;
    background: #d9d9d9;
    top: -1px;
    left: 28px;
}

.btn {
    box-shadow: none;
    background: #fff;
    height: 38px;
    min-width: 70px;
    padding: 0 10px;
    color: #888A9A;
    border: 1px solid #EDEDF2;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    cursor: pointer;
    text-transform: capitalize;
    font-size: 14px;

    &:hover {
        background: #e5e5e5;
    }
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px !important;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #fff;
            outline: none;
            font-size: 14px;
            margin: 0;
            display: flex;
            align-items: center;
            position: relative;
            padding: 0 12px;

            &:before, &:after {
                border: none !important;
            }
        }

        .v-messages {
            min-height: 3px !important;
        }
    }
}

.operator {
    width: 62px;

    ::v-deep .v-select.v-text-field input {
        display: none !important;
    }

    ::v-deep .v-input__append-inner {
        margin-top: 0 !important;
    }
}

.ScenarioComboBox {
    border-color: #fff !important;
    
    ::v-deep .v-select__selections {
        justify-content: flex-start !important;
    }

    ::v-deep .v-input__slot {
        padding: 0 !important;
    }

    ::v-deep span.v-chip__content {
        display: inline-block;
        max-width: 120px;
        white-space: nowrap;
        overflow: hidden !important;
        text-overflow: ellipsis;
    }
}

.ScenarioInput {
    border-color: #fff !important;
}

.btn-add-group {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border: 1px solid #d9d9d9;
    border-radius: 5px;
    cursor: pointer;
    font-size: 13px;
    font-family: "open sans semi", serif;
    color: #727885;
    user-select: none;
    
    .icon-add-group {
        transform: scale(1.15);
        height: 16px;
        margin-right: 6px;
        
        &:before {
            content: url("/img/scenario/add_grey.svg");
        }
    }
    
    &:hover {
        color: #0052cc;
        border-color: #EEF6FF !important;
        .icon-add-group:before {
            content: url("/img/scenario/add_blue.svg");
        }
        background: #EEF6FF;
    }
}

::v-deep {
    .v-select {
        .v-input__slot {
            padding: 0 6px 0 12px;
        }
    }

    .v-text-field {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    .v-select__selection--comma {
        margin: 0 !important;
    }

    .v-select__selections {
        justify-content: center;
    }
}
</style>
